import { useState } from 'react';
import { useToast } from '../../context_providers/toast_context/ToastContext';
import { useOrganizationUpdaterProvider } from '../../context_providers/organization_context/OrganizationProvider';
import { useTranslation } from 'react-i18next';
import {
  ErrorStringKeys,
  SuccessStringKeys,
  StringKeys,
} from '../../resources/strings/StringKeys';
import type {
  EmergencyConfigurationOnAzure,
  AddEmergencyConfigRequest,
  UpdateEmergencyConfigRequest,
} from '../../../generated-client/models';
import {
  addEmergencyConfiguration,
  updateEmergencyConfiguration,
  deleteEmergencyConfiguration,
  getAllEmergencyConfigurations,
  getEmergencyConfigurationByOrgCodeAndId,
} from '../../repository/EmergencyConfigDAO';

type OperationContext =
  | { operation: 'create'; data: AddEmergencyConfigRequest }
  | { operation: 'update'; data: UpdateEmergencyConfigRequest }
  | { operation: 'delete'; data: EmergencyConfigurationOnAzure };

interface UseEmergencyConfigOperationsOptions {
  onSuccess?: (config: EmergencyConfigurationOnAzure) => void;
  onError?: (error: unknown, context: OperationContext) => void;
  updateLocalState?: (
    operation: 'add' | 'update' | 'delete',
    config: EmergencyConfigurationOnAzure,
  ) => void;
}

export const useEmergencyConfigOperations = (
  options: UseEmergencyConfigOperationsOptions = {},
) => {
  const [loading, setLoading] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const { loadEmergencyConfigs } = useOrganizationUpdaterProvider();
  const { showErrorToast, showSuccessToast } = useToast();
  const { t } = useTranslation();

  const createConfig = async (
    request: AddEmergencyConfigRequest,
  ): Promise<EmergencyConfigurationOnAzure | null> => {
    if (inProgress) return null;
    const { onSuccess, onError, updateLocalState } = options;
    try {
      setLoading(true);
      setInProgress(true);
      const saved = await addEmergencyConfiguration(request);
      showSuccessToast(
        t(SuccessStringKeys.SUCCESSFULLY_ADDED_EMERGENCY_CONFIGURATION, { configuration: request.spec_name }),
      );
      updateLocalState?.('add', saved);
      onSuccess?.(saved);
      loadEmergencyConfigs();
      return saved;
    } catch (error) {
      showErrorToast(
        t(ErrorStringKeys.FAILED_TO_ADD_EMERGENCY_CONFIGURATION, { configuration: request.spec_name }),
      );
      onError?.(error, { operation: 'create', data: request });
      return null;
    } finally {
      setLoading(false);
      setInProgress(false);
    }
  };

  const updateConfig = async (
    request: UpdateEmergencyConfigRequest,
  ): Promise<EmergencyConfigurationOnAzure | null> => {
    if (inProgress) return null;
    const { onSuccess, onError, updateLocalState } = options;
    try {
      setLoading(true);
      setInProgress(true);
      const result = await updateEmergencyConfiguration(request);
      showSuccessToast(
        t(SuccessStringKeys.SUCCESSFULLY_UPDATED_EMERGENCY_CONFIGURATION, { configuration: request.spec_name }),
      );
      updateLocalState?.('update', result);
      onSuccess?.(result);
      loadEmergencyConfigs();
      return result;
    } catch (error) {
      showErrorToast(
        t(ErrorStringKeys.FAILED_TO_UPDATE_EMERGENCY_CONFIGURATION, { configuration: request.spec_name }),
      );
      onError?.(error, { operation: 'update', data: request });
      return null;
    } finally {
      setLoading(false);
      setInProgress(false);
    }
  };

  const deleteConfig = async (
    config: EmergencyConfigurationOnAzure,
  ): Promise<boolean> => {
    if (inProgress) return false;
    const { onSuccess, onError, updateLocalState } = options;
    try {
      setLoading(true);
      setInProgress(true);
      await deleteEmergencyConfiguration({ org_code: config.org_code, config_id: config.id });
      showSuccessToast(
        t(SuccessStringKeys.SUCCESSFULLY_REMOVED_EMERGENCY_CONFIGURATION, { configuration: config.spec_name }),
      );
      updateLocalState?.('delete', config);
      onSuccess?.(config);
      loadEmergencyConfigs();
      return true;
    } catch (error: unknown) {
      const errorMessage =
        (error as { message?: string })?.message ?? String(error);
      if (errorMessage.includes('Cannot delete org_spec with org_users assigned')) {
        showErrorToast(
          t(StringKeys.USERS_CURRENTLY_ASSIGNED_TO_CONFIGURATION, { configuration: config.spec_name }),
        );
      } else {
        showErrorToast(
          t(ErrorStringKeys.FAILED_TO_REMOVE_EMERGENCY_CONFIGURATION, { configuration: config.spec_name }),
        );
      }
      onError?.(error, { operation: 'delete', data: config });
      return false;
    } finally {
      setLoading(false);
      setInProgress(false);
    }
  };

  const fetchConfigs = async (
    orgCode: string,
  ): Promise<EmergencyConfigurationOnAzure[] | null> => {
    try {
      setLoading(true);
      return await getAllEmergencyConfigurations(orgCode);
    } catch {
      showErrorToast(t(StringKeys.SOMETHING_WENT_WRONG));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchConfigById = async (
    orgCode: string,
    configId: string,
  ): Promise<EmergencyConfigurationOnAzure | null> => {
    try {
      setLoading(true);
      return await getEmergencyConfigurationByOrgCodeAndId(orgCode, configId);
    } catch {
      showErrorToast(t(StringKeys.SOMETHING_WENT_WRONG));
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { createConfig, updateConfig, deleteConfig, fetchConfigs, fetchConfigById, loading, inProgress };
};
